import * as _ from 'lodash';
import * as moment from 'moment';

import { Common } from '@data/models/common.model';
import { EmbeddedProject, EmbeddedUser } from '@data/models/embed.model';
import { Project } from '@data/models/project.model';
import { concatMap, delay, Observable, of } from 'rxjs';

export type PartialRequired<T, K extends keyof T> = Partial<T> & Pick<T, K>;

/**
 * A duration as a tuple { value, unit } where value is a number and unit a string
 * @param duration A string indicating a duration, like '12 months', '1 year'
 */
export function parseDuration(duration: string): { value: number; unit: string } {
    try {
        const regex = /^\s*(\d+)\s*([a-zA-Z]+)\s*$/g;
        const [, strValue, unit] = regex.exec(duration);
        return { value: +strValue, unit };
    } catch (error) {
        return { value: 0, unit: 'months' };
    }
}

/**
 * Translate duration from API (in english) to the destination locale
 * @param duration
 * @param locale
 */
export function translateDuration(duration: string = '0 months', locale: string = 'fr'): string {
    // I18N: This function is a hack - use i18n and proper localization techniques instead when possible
    const lang = locale.substring(0, 2).toLowerCase();
    const LANG_UNITS = {
        fr: {
            s: 's', second: 'seconde', seconds: 'secondes',
            m: 'm', minute: 'minute', minutes: 'minutes',
            h: 'h', hour: 'heure', heures: 'heures',
            d: 'j', day: 'jour', days: 'jours',
            w: 'sem', week: 'semaine', weeks: 'semaines',
            M: 'M', month: 'mois', months: 'mois',
            y: 'an', year: 'an', years: 'ans'
        }
    };
    duration = duration.toLowerCase();
    const d = parseDuration(duration);
    if (d.value === 0) {
        return lang === 'fr' ? 'inconnue' : 'unknown';
    }

    const unit = lang === 'fr' ? LANG_UNITS.fr[d.unit] : d.unit;
    return unit ? `${d.value} ${unit}` : 'inconnue';
}

/**
 * Build a table of pageSizeOptions to be used with a mat-paginator
 * from the length of an array of objects
 *
 * @param arr An array of objects to evaluate
 */
export function buildPageSizeOptions<T>(arr: T[]): number[] {
    if (!arr || arr.length === 0) {
        return [];
    }

    const res: number[] = [];
    const len = arr.length;

    if (len > 5) {
        res.push(5);
    }
    if (len > 10) {
        res.push(10);
    }
    if (len > 25) {
        res.push(25);
    }
    if (len > 100) {
        res.push(100);
    }
    res.push(len);

    return res;
}
interface UserTracking { isDeleted: boolean; fullName: string }
const DELETED_USER_FR = '(utilisateur supprimé)';
const DELETED_USER_EN = '(deleted user)';
export interface Tracking {
    createdAt: string;
    createdBy: UserTracking;
    updatedAt: string;
    updatedBy: UserTracking;
}

export function getTracking(obj: Common, locale: string = 'fr-FR', createdBy?: EmbeddedUser, updatedBy?: EmbeddedUser): Tracking {
    return {
        createdAt: new Date(obj.createdAt).toLocaleString(locale),
        // Test is needed on fullName since EmbeddedUser can be a `new User` by default (case where user does not exists)
        createdBy: { isDeleted: !Boolean(createdBy?.fullName), fullName: createdBy?.fullName ? createdBy.fullName : obj.createdBy },
        updatedAt: new Date(obj.updatedAt).toLocaleString(locale),
        updatedBy: { isDeleted: !Boolean(updatedBy?.fullName), fullName: updatedBy?.fullName ? updatedBy.fullName : obj.updatedBy }
    } as Tracking;
}

// I18N: replace this when we have proper localization
export function getTrackingTooltip(
    obj: Common, meta: { deletedDisplayed: boolean; createdBy: EmbeddedUser; updatedBy: EmbeddedUser } = undefined, locale: string = 'fr-FR'
): string {
    if (!obj) {
        return '';
    }
    const t = getTracking(obj, locale, meta?.createdBy, meta?.updatedBy);
    let tooltip = '';
    tooltip += `${getTrackingCreateTooltip(obj, t, locale, meta?.deletedDisplayed)}\n`;
    tooltip += getTrackingUpdateTooltip(obj, t, locale);
    return tooltip;
}

export function getTrackingCreateTooltip(obj: Common, tracking: Tracking, locale: string = 'fr-FR', deletedDisplayed: boolean = false): string {
    if (!obj || !tracking) {
        return '';
    }
    const t = tracking ?? getTracking(obj, locale);
    const isDeletedDisplayed = deletedDisplayed && t.createdBy.isDeleted;
    return (locale.substr(0, 2).toLowerCase() === 'fr') ?
        t.createdBy.fullName ? `Créé le ${t.createdAt} par ${t.createdBy.fullName} ${isDeletedDisplayed ? DELETED_USER_FR : ''}` : `Créé le ${t.createdAt}` :
        t.createdBy.fullName ? `Created at ${t.createdAt} by ${t.createdBy.fullName} ${isDeletedDisplayed ? DELETED_USER_EN : ''}` : `Created at ${t.createdAt}`;
}

export function getTrackingUpdateTooltip(obj: Common, tracking: Tracking, locale: string = 'fr-FR', deletedDisplayed: boolean = false): string {
    if (!obj) {
        return '';
    }
    const t = tracking ?? getTracking(obj, locale);
    const isDeletedDisplayed = deletedDisplayed && t.updatedBy.isDeleted;
    return (locale.substr(0, 2).toLowerCase() === 'fr') ?
        t.updatedBy.fullName ?
            `Mis à jour le ${t.updatedAt} par ${t.updatedBy.fullName} ${isDeletedDisplayed ? DELETED_USER_FR : ''}` : `Mis à jour le ${t.updatedAt}` :
        t.updatedBy.fullName ? `Updated at ${t.updatedAt} by ${t.updatedBy.fullName} ${isDeletedDisplayed ? DELETED_USER_EN : ''}` : `Updated at ${t.updatedAt}`;
}

/**
 * lodash customizer
 * A function which is invoked to produce the merged values of the destination and source properties
 * This customizer merges array values
 * Usage: _.mergeWith(obj, sources, concatArrays)
 * @param objValue
 * @param srcValue
 */
export function concatArrays(objValue, srcValue) {
    if (_.isArray(objValue)) {
        return objValue.concat(srcValue);
    }
}

export function toCaptionAt(obj: Common): string {
    return `${obj.id} | Créé le ${obj.createdAt ? moment(obj.createdAt).format('D MMMM YYYY à H:mm') : ''}` +
        (obj.updatedAt ? ` | Mis à jour le ${moment(obj.updatedAt).format('D MMMM YYYY à H:mm')}` : '') +
        (obj.deletedAt ? ` | Désactivé le ${moment(obj.deletedAt).format('D MMMM YYYY à H:mm')}` : '');
}

export function toTooltipBy(obj: Common & {
    $createdBy?: EmbeddedUser;
    $updatedBy?: EmbeddedUser;
    $deletedBy?: EmbeddedUser;
}): string {
    return `Créé par ${obj.$createdBy?.fullName || '?'}` +
        (obj.updatedBy ? ` | Mis à jour par ${obj.$updatedBy?.fullName || '?'}` : '') +
        (obj.deletedBy ? ` | Désactivé par ${obj.$deletedBy?.fullName || '?'}` : '');
}

export function createButtonExportCsv(rawData: string, fileName: string): void {
    const downloadButton = document.createElement('a');
    const csvData = new Blob([rawData], { type: 'text/csv' });
    downloadButton.href = URL.createObjectURL(csvData);
    downloadButton.target = '_blank';
    downloadButton.download = `${fileName}_${moment().format('DD_MM_YYYY')}.csv`;
    downloadButton.click();
    downloadButton.remove();
}

export function createButtonExportBase64(options: { rawData: string; fileName: string; type: string; ext: string }): void {
    const downloadButton = document.createElement('a');
    downloadButton.href = `data:${options.type};base64,${options.rawData}`;
    downloadButton.target = '_blank';
    downloadButton.download = `${options.fileName}_${moment().format('DD_MM_YYYY')}.${options.ext}`;
    downloadButton.click();
    downloadButton.remove();
}

export interface ProjectAvatar {
    projectAvatar?: {
        link: string | null;
        text: string;
        caption: string;
        tooltip: string;
    };
}

export function withProjectsAvatar<T>(item: T, projects: Array<Project | EmbeddedProject>): T & ProjectAvatar {
    const [firstProject, ...othersProjects] = projects;
    return {
        ...item,
        projectAvatar: {
            link: firstProject ? '/projects/' + firstProject.id : null,
            text: firstProject?.name || '',
            caption: !othersProjects.length && othersProjects.length === 0
                ? ''
                : `+ ${othersProjects.length} projet${othersProjects.length > 1 ? 's' : ''}`,
            tooltip: othersProjects.length
                ? `Autres projet${othersProjects.length > 1 ? 's' : ''} :\n` +
                othersProjects.map(project => `- ${project.name} (${project.id})`).join('\n')
                : ''
        },
    };
}

export function scrollIntoView(htmlQuery: string): void {
    const firstElmt: HTMLElement = document.querySelector(htmlQuery);
    firstElmt.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

export async function blobFileToString(blobFile: Blob): Promise<string> {
    const fileReader = new FileReader();
    const stringifiedFile = await new Promise<string>((resolve, reject) => {
        fileReader.onload = () => {
            const binaryString = fileReader.result as string;
            resolve(btoa(binaryString));
        };
        fileReader.onerror = reject;
        fileReader.readAsBinaryString(blobFile);
    });
    return stringifiedFile;
}

export function ensureMinTime<T>(source$: Observable<T>, ms: number): Observable<T> {
    return source$.pipe(
        // Garantit que chaque émission prend au moins 'ms' millisecondes
        concatMap(value => of(value).pipe(
            delay(ms)
        ))
    );
}
